/**
 * ArtByLocation.jsx — Phase 5 "By Location" tab (2026-02).
 *
 * List view = stacked location blocks with a horizontal scenes strip,
 * inline scene expansion panel, actual location photos, mood board,
 * palette, address/map row and (placeholder) Base Kit section.
 *
 * Cards view = 3-column grid of location cards.
 *
 * Design decisions locked with user:
 *   • 2c — Hybrid lazy-promote for `locations` collection.
 *   • 7  — Map = link-only MVP (Google Maps URL, no iframe/API key).
 *   • S1 — Look & Feel reuses color_palettes + mood_board with
 *          scope="location" + scope_id=<location_id>. Same pattern
 *          established in Phase 4.
 *   • Base Kit section is a placeholder — real inheritance lands
 *          in Phase 6.5 after the Assets model exists.
 *
 * Editing on a scene happens via the SAME right-side drawer used
 * by ArtByScene — imported as `Drawer` from that module. No
 * duplication.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  MapPin, ChevronLeft, ChevronRight, Plus, Upload, ExternalLink,
  Layers, X, Search,
} from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import { Input } from '../../components/ui/input';
import { formatSceneNumber } from '../../utils/sceneNumber';
import { useProjectRole } from '../../hooks/useProjectRole';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  ART_COL_TOKENS, PANEL_DIVIDER, TypeBadge, TodBadge,
  PropCol, Lightbox,
  ReqPriorityPill, ReqCategoryPill, ReqStatusPill,
} from './artShared';
import { Drawer as SceneDrawer } from './ArtByScene';
import { Link2 } from 'lucide-react';
import { useAssetActions } from './AssetActionsContext';
import { useRequirementActions } from './RequirementActionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Bucketing helpers (mirror ArtByScene) ─────────────────────────────
function bucketOf(item) {
  const c = (item.category || '').toUpperCase();
  if (c === 'PICTURE_VEHICLE') return 'picture_vehicle';
  if (c === 'ART_SET_DRESSING') return 'set_dressing';
  // Phase 4 (2026-02) — nullable `is_action_prop` override.
  // Mirrors the ArtByScene.jsx rule verbatim; see that file for the
  // full case matrix.
  if (item.is_action_prop === true)  return 'action_props';
  if (item.is_action_prop === false) return 'scene_props';
  if (c === 'PROP')       return 'action_props';
  if (c === 'SCENE_PROP') return 'scene_props';
  return null;
}
function bucketsForItems(items) {
  const b = { action_props: [], scene_props: [], set_dressing: [], picture_vehicle: [] };
  for (const it of (items || [])) {
    const k = bucketOf(it);
    if (k) b[k].push(it);
  }
  return b;
}

// ── Google Maps link builder (Decision #7) ────────────────────────────
function mapsUrl(loc) {
  if (!loc) return null;
  const c = loc.coordinates;
  if (c && typeof c.lat === 'number' && typeof c.lng === 'number') {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.lat},${c.lng}`)}`;
  }
  if (loc.address && loc.address.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address.trim())}`;
  }
  return null;
}

// ── Compact scene card used in the horizontal strip ──────────────────
function SceneStripCard({ scene, buckets, expanded, onSelect }) {
  const firstPhoto = useMemo(() => {
    for (const k of ['action_props', 'scene_props', 'set_dressing', 'picture_vehicle']) {
      for (const it of buckets[k]) {
        if (it.image_paths?.[0]) return it.image_paths[0];
      }
    }
    return null;
  }, [buckets]);
  const counts = {
    a: buckets.action_props.length,
    s: buckets.scene_props.length,
    d: buckets.set_dressing.length,
  };
  return (
    <button
      type="button"
      onClick={() => onSelect(scene)}
      className={`shrink-0 w-[220px] h-[140px] rounded-lg border ${expanded ? 'border-[var(--color-accent)]' : 'border-[var(--color-border)]'} bg-[var(--color-panel)] hover:border-[var(--color-accent)] p-2 relative overflow-hidden text-left transition`}
      data-testid={`art-loc-scene-card-${scene.scene_number}`}
    >
      <div className="absolute top-1.5 left-1.5">
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--color-scene-props)] text-black">
          #{formatSceneNumber(scene.scene_number)}
        </span>
      </div>
      <div className="absolute top-1.5 right-1.5 flex flex-col gap-0.5 items-end">
        <TypeBadge type={scene.location_type} />
        <TodBadge tod={scene.time_of_day} />
      </div>
      <div className="flex mt-6 gap-2">
        <div className="w-[60px] h-[60px] rounded overflow-hidden bg-[var(--color-bg)] border border-[var(--color-border)] shrink-0">
          {firstPhoto ? (
            <AuthImage path={firstPhoto} className="w-full h-full object-cover" alt="" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[var(--color-text-mute)]">
              <MapPin className="w-6 h-6" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-[var(--color-text-sec)] line-clamp-2 leading-snug">
            {scene.synopsis_en || scene.synopsis || '—'}
          </div>
        </div>
      </div>
      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between">
        <div className="text-[10px] text-[var(--color-text-dim)]">A:{counts.a} · S:{counts.s} · D:{counts.d}</div>
        <span className="text-[10px] text-[var(--color-accent)]">{expanded ? '▴' : '▾'}</span>
      </div>
    </button>
  );
}

// ── Inline expanded panel (read-only preview + "Open full details") ──
function ExpandedScenePanel({ scene, buckets, palettes, moods, onClose,
                              onOpenDrawer, onPhotoClick, locPaletteCount }) {
  if (!scene) return null;
  const scenePalOverrides = palettes.length > 0 && locPaletteCount > 0;
  return (
    <div
      className="mx-4 mb-3 rounded-lg border border-[var(--color-border)]"
      style={{ background: 'rgba(99, 102, 241, 0.05)' }}
      data-testid="art-loc-expanded-panel"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)]">
        <span className="text-sm font-bold text-[var(--color-accent)]">Scene {formatSceneNumber(scene.scene_number)}</span>
        <TypeBadge type={scene.location_type} />
        <TodBadge tod={scene.time_of_day} />
        <span className="text-xs text-[var(--color-text-sec)] truncate">· {scene.location_name}</span>
        <button
          type="button"
          onClick={() => onOpenDrawer(scene)}
          className="ml-auto text-[11px] px-2 py-1 rounded border border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-black transition"
          data-testid="art-loc-open-details">
          Open full breakdown ▸
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-[var(--color-text-dim)] hover:text-white"
          data-testid="art-loc-expanded-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-3 py-2 border-b border-[var(--color-border)]">
        <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Synopsis</div>
        <div className="text-[12px] text-[var(--color-text)] whitespace-pre-wrap leading-relaxed">
          {scene.synopsis_en || scene.synopsis || <span className="italic text-[var(--color-text-mute)]">—</span>}
        </div>
      </div>
      <div className="px-3 py-2 border-b border-[var(--color-border)]">
        <div className="grid grid-cols-3" style={{ columnGap: '1px', background: PANEL_DIVIDER }}>
          <PropCol title={ART_COL_TOKENS.action_props.label} colKey="action_props" items={buckets.action_props} onPhotoClick={onPhotoClick} drawerMode />
          <PropCol title={ART_COL_TOKENS.scene_props.label}  colKey="scene_props"  items={buckets.scene_props}  onPhotoClick={onPhotoClick} drawerMode />
          <PropCol title={ART_COL_TOKENS.set_dressing.label} colKey="set_dressing" items={buckets.set_dressing} onPhotoClick={onPhotoClick} drawerMode />
        </div>
      </div>
      {buckets.picture_vehicle.length > 0 && (
        <div className="px-3 py-2 border-b border-[var(--color-border)]">
          <PropCol title={ART_COL_TOKENS.picture_vehicle.label} colKey="picture_vehicle" items={buckets.picture_vehicle} onPhotoClick={onPhotoClick} drawerMode />
        </div>
      )}
      {scene.art_notes && (
        <div className="px-3 py-2 border-b border-[var(--color-border)] text-[11px] text-[var(--color-text-sec)]">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mr-1">Notes:</span>
          {scene.art_notes}
        </div>
      )}
      {scene.additional_notes && (
        <div className="px-3 py-2 border-b border-[var(--color-border)] text-[11px] text-[var(--color-text)]"
             style={{ borderLeft: '4px solid var(--color-accent)', background: 'var(--color-accent-soft)' }}>
          <span className="text-[9px] font-bold uppercase tracking-wider mr-1" style={{ color: 'var(--color-accent)' }}>
            Additional Notes:
          </span>
          {scene.additional_notes}
        </div>
      )}
      {palettes.length > 0 && (
        <div className="px-3 py-2">
          <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">
            Scene palette
            {scenePalOverrides && <span className="ml-1 text-[9px] italic text-[var(--color-text-dim)]">(overrides location)</span>}
          </div>
          {palettes.map(p => (
            <div key={p.id} className="mb-1">
              <div className="text-[10px] text-[var(--color-text-sec)] mb-0.5">{p.name}</div>
              <div className="flex gap-1 flex-wrap">
                {(p.colors || []).map((c, i) => (
                  <div key={i} className="w-5 h-5 rounded border border-[var(--color-border)]" style={{ background: c.hex }} title={c.label || c.hex} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── One location block (List view) ────────────────────────────────────
function LocationBlock({
  location, scenes, itemsByScene, palettes, moods, locationPhotos,
  assets, canWrite, onOpenDrawer, projectId, token, onDataChange, onPhotoClick,
}) {
  const [selectedScene, setSelectedScene] = useState(null);
  const stripRef = useRef(null);
  const [address, setAddress] = useState(location.address || '');
  const [editingAddress, setEditingAddress] = useState(false);
  const [addingColor, setAddingColor] = useState(false);
  const [colorDraft, setColorDraft] = useState('#e8c547');
  const [colorName, setColorName] = useState('Location palette');
  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);
  const { openAssetModal } = useAssetActions();

  const locScenes = useMemo(() =>
    (scenes || []).slice().sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0)),
  [scenes]);

  const scrollStrip = (delta) => {
    if (!stripRef.current) return;
    stripRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  };

  // Ensure a real locations doc exists (lazy-promote). Returns the id.
  const ensureLocationId = async () => {
    if (location.id) return location.id;
    const key = encodeURIComponent(location.name);
    const r = await authAxios.patch(`/projects/${projectId}/art/locations/${key}`, {});
    onDataChange?.('location', r.data);
    return r.data.id;
  };

  const saveAddress = async () => {
    try {
      const key = encodeURIComponent(location.id || location.name);
      const r = await authAxios.patch(`/projects/${projectId}/art/locations/${key}`,
        { address: address.trim() });
      onDataChange?.('location', r.data);
      setEditingAddress(false);
      toast.success('Address saved');
    } catch (e) { console.error(e); toast.error('Save failed'); }
  };

  // Photo upload → uses gallery_router's `POST /projects/{pid}/gallery`
  // which scopes by department + location_name and routes through
  // register_upload_file() internally.
  const photoInputRef = useRef(null);
  const uploadLocationPhoto = () => photoInputRef.current?.click();
  const onPhotoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios.post(`/projects/${projectId}/gallery`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { department: 'ART', location_name: location.name },
      });
      onDataChange?.('gallery_ref', r.data);
      toast.success('Photo added');
    } catch (err) { console.error(err); toast.error('Upload failed'); }
  };

  // Mood board upload — scoped mood_board via Phase 4 endpoint
  const moodInputRef = useRef(null);
  const uploadMood = () => moodInputRef.current?.click();
  const onMoodFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const lid = await ensureLocationId();
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios.post(`/projects/${projectId}/mood-board/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { department: 'ART', scope: 'location', scope_id: lid },
      });
      onDataChange?.('mood', r.data);
      toast.success('Mood image added');
    } catch (err) { console.error(err); toast.error('Upload failed'); }
  };

  const saveColor = async () => {
    try {
      const lid = await ensureLocationId();
      // Simple case: extend palette by 1 color under a name. If a
      // palette exists for this location we could push into it; MVP
      // creates a fresh 1-color entry so the swatch strip grows.
      const r = await authAxios.post(`/projects/${projectId}/palettes`, {
        department: 'ART',
        name: colorName || 'Location palette',
        colors: [{ hex: colorDraft }],
        source: 'manual',
        scope: 'location',
        scope_id: lid,
      });
      onDataChange?.('palette', r.data);
      setAddingColor(false);
      toast.success('Color added');
    } catch (e) { console.error(e); toast.error('Save failed'); }
  };

  const stripTooFew = locScenes.length < 4;
  const mapsHref = mapsUrl(location);

  return (
    <div className="border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)]" data-testid={`art-loc-block-${location.name.toLowerCase().replace(/\s+/g,'-')}`}>
      {/* A. Header — Commit B (Phase 12a.1 Item 7-i, 2026-02):
          Vertical stack. Line 1 carries metadata badges + inline stats
          + right-side action buttons. Line 2 gives the location name
          full width with a generous `--sp-*` gap above it so the name
          reads as the primary label and not a runt among badges. NO
          `#N` scene-number prefix — that lives on the scene cards
          below, not on the location header. */}
      <div
        className="px-3 py-2 bg-[var(--color-bg)] border-b border-[var(--color-border)] rounded-t-lg flex flex-col"
        style={{ rowGap: 'var(--sp-3)' }}
      >
        {/* Line 1 — metadata row (badges + counts + action buttons) */}
        <div className="flex items-center gap-2 flex-wrap">
          {location.scene_int_ext === 'MIXED' ? (
            <>
              <TypeBadge type="INT" />
              <TypeBadge type="EXT" />
            </>
          ) : (
            <TypeBadge type={location.scene_int_ext || 'INT'} />
          )}
          <span className="text-[10px] text-[var(--color-text-dim)]">
            📷 {location.photo_count} photos · 🎬 {location.scene_count} scenes · 🎨 palette{location.palette_count > 0 ? ` (${location.palette_count})` : ''} · 🗺 map{mapsHref ? '' : ' (—)'}
          </span>
          <a
            href={mapsHref || '#'}
            onClick={(e) => { if (!mapsHref) { e.preventDefault(); toast.info('Set an address to open in Maps'); } }}
            target="_blank" rel="noopener noreferrer"
            className={`ml-auto text-[11px] px-2 py-1 rounded border ${mapsHref ? 'border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-black' : 'border-[var(--color-border)] text-[var(--color-text-dim)] cursor-not-allowed'}`}
            data-testid="art-loc-maps-link"
            title={mapsHref ? 'Open in Google Maps' : 'Set an address to open in Maps'}>
            🗺 Open in Google Maps <ExternalLink className="w-3 h-3 inline ml-0.5" />
          </a>
          {canWrite && (
            <button
              type="button"
              onClick={async () => {
                const lid = await ensureLocationId();
                openAssetModal({
                  prefill: { linked_location_ids: [lid], category: 'set_dressing' },
                });
              }}
              className="text-[11px] px-2 py-1 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
              data-testid="art-loc-add-asset">
              + Add asset
            </button>
          )}
        </div>
        {/* Line 2 — location name (primary label, generous vertical
            gap from the badges above via `rowGap` on the container) */}
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-[var(--color-accent)] shrink-0" />
          <span
            className="text-base font-bold text-[var(--color-text)] truncate"
            data-testid="art-loc-name"
          >
            {location.name}
          </span>
        </div>
      </div>

      {/* B. Scenes strip */}
      <div className="px-3 py-2 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)]">
            Scenes at this location ({locScenes.length})
          </span>
          <div className="ml-auto flex items-center gap-1">
            <span className="text-[10px] text-[var(--color-text-dim)]">Sort: Scene # ▾</span>
            <button type="button" onClick={() => scrollStrip(-660)} className="text-[var(--color-text-dim)] hover:text-white px-1" data-testid="art-loc-strip-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => scrollStrip(660)} className="text-[var(--color-text-dim)] hover:text-white px-1" data-testid="art-loc-strip-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {stripTooFew ? (
          <div className="flex gap-2 flex-wrap">
            {locScenes.map(s => (
              <SceneStripCard key={s.id} scene={s}
                buckets={bucketsForItems(itemsByScene[s.id])}
                expanded={selectedScene?.id === s.id}
                onSelect={(sc) => setSelectedScene(selectedScene?.id === sc.id ? null : sc)} />
            ))}
          </div>
        ) : (
          <div className="relative">
            <div
              ref={stripRef}
              className="flex gap-2 overflow-x-auto scrollbar-thin py-1"
              data-testid="art-loc-scenes-strip"
              style={{ scrollbarWidth: 'thin' }}
            >
              {locScenes.map(s => (
                <SceneStripCard key={s.id} scene={s}
                  buckets={bucketsForItems(itemsByScene[s.id])}
                  expanded={selectedScene?.id === s.id}
                  onSelect={(sc) => setSelectedScene(selectedScene?.id === sc.id ? null : sc)} />
              ))}
            </div>
            <div className="text-[10px] text-[var(--color-text-mute)] mt-1">Shift+scroll or use ‹ › to browse</div>
          </div>
        )}
      </div>

      {/* C. Expanded scene panel (single-expansion) */}
      {selectedScene && (
        <ExpandedScenePanel
          scene={selectedScene}
          buckets={bucketsForItems(itemsByScene[selectedScene.id])}
          palettes={(palettes || []).filter(p => p.scope === 'scene' && p.scope_id === selectedScene.id)}
          moods={(moods || []).filter(m => m.scope === 'scene' && m.scope_id === selectedScene.id)}
          locPaletteCount={(palettes || []).filter(p => p.scope === 'location' && p.scope_id === location.id).length}
          onClose={() => setSelectedScene(null)}
          onOpenDrawer={onOpenDrawer}
          onPhotoClick={onPhotoClick}
        />
      )}

      {/* D. Actual Location Photos */}
      <div className="px-3 py-2 border-b border-[var(--color-border)]">
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)]">Actual Location Photos</span>
          <span className="text-[10px] italic text-[var(--color-text-mute)]">(from Locations dept)</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {locationPhotos.map((r) => (
            <button key={r.id} type="button"
              className="w-[120px] h-[120px] rounded overflow-hidden border border-[var(--color-border)]"
              onClick={() => onPhotoClick?.({ path: r.storage_path, itemName: r.original_filename || 'Location photo', locationName: location.name })}
              data-testid="art-loc-photo-thumb">
              <AuthImage path={r.storage_path} className="w-full h-full object-cover" alt="" />
            </button>
          ))}
          {locationPhotos.length === 0 && (
            <span className="text-[11px] italic text-[var(--color-text-mute)] self-center">
              {canWrite ? 'No photos yet.' : 'No photos yet.'}
            </span>
          )}
          {canWrite && (
            <button type="button"
              onClick={uploadLocationPhoto}
              className="w-[120px] h-[120px] rounded border border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-accent)] text-[var(--color-text-dim)] hover:text-[var(--color-accent)] flex flex-col items-center justify-center gap-1 text-[10px]"
              data-testid="art-loc-upload-photo">
              <Upload className="w-4 h-4" />
              Upload
            </button>
          )}
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoFileChange} className="hidden" />
      </div>

      {/* E. Mood Board */}
      <div className="px-3 py-2 border-b border-[var(--color-border)]">
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)]">Mood Board</span>
          <span className="text-[10px] italic text-[var(--color-text-mute)]">(art dept curated)</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(moods || []).filter(m => m.scope === 'location' && m.scope_id === location.id).map(m => (
            <button key={m.id} type="button"
              className="w-[120px] h-[120px] rounded overflow-hidden border border-[var(--color-border)]"
              onClick={() => onPhotoClick?.({ path: m.storage_path, itemName: m.caption || 'Mood', locationName: location.name })}
              data-testid="art-loc-mood-thumb">
              <AuthImage path={m.storage_path} className="w-full h-full object-cover" alt="" />
            </button>
          ))}
          {(moods || []).filter(m => m.scope === 'location' && m.scope_id === location.id).length === 0 && (
            <span className="text-[11px] italic text-[var(--color-text-mute)] self-center">No mood images yet.</span>
          )}
          {canWrite && (
            <button type="button"
              onClick={uploadMood}
              className="w-[120px] h-[120px] rounded border border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-accent)] text-[var(--color-text-dim)] hover:text-[var(--color-accent)] flex flex-col items-center justify-center gap-1 text-[10px]"
              data-testid="art-loc-add-mood">
              <Plus className="w-4 h-4" />
              Add
            </button>
          )}
        </div>
        <input ref={moodInputRef} type="file" accept="image/*" onChange={onMoodFileChange} className="hidden" />
      </div>

      {/* F. Palette + Map row */}
      <div className="grid grid-cols-1 md:grid-cols-2 border-b border-[var(--color-border)]">
        <div className="px-3 py-2 border-r border-[var(--color-border)]">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mb-1.5">Palette</div>
          <div className="flex gap-1 flex-wrap items-center">
            {(palettes || []).filter(p => p.scope === 'location' && p.scope_id === location.id).flatMap(p => (p.colors || [])).map((c, i) => (
              <button key={i} type="button" onClick={() => {
                if (c.hex) { navigator.clipboard?.writeText(c.hex); toast.success(`${c.hex} copied`); }
              }} className="w-6 h-6 rounded border border-[var(--color-border)]"
                 style={{ background: c.hex }} title={c.label || c.hex}
                 data-testid="art-loc-palette-swatch" />
            ))}
            {(palettes || []).filter(p => p.scope === 'location' && p.scope_id === location.id).length === 0 && !addingColor && (
              <span className="text-[11px] italic text-[var(--color-text-mute)]">No palette yet.</span>
            )}
            {canWrite && !addingColor && (
              <button type="button" onClick={() => setAddingColor(true)}
                className="ml-1 text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                data-testid="art-loc-palette-add">+ color</button>
            )}
            {addingColor && (
              <div className="flex items-center gap-1">
                <input type="color" value={colorDraft} onChange={(e) => setColorDraft(e.target.value)}
                  className="w-6 h-6 rounded border border-[var(--color-border)] bg-transparent cursor-pointer"
                  data-testid="art-loc-palette-color-input" />
                <input value={colorName} onChange={(e) => setColorName(e.target.value)}
                  className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-1.5 py-0.5 text-[11px] text-[var(--color-text)] w-32"
                  placeholder="Palette name" data-testid="art-loc-palette-name" />
                <button type="button" onClick={saveColor}
                  className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-accent)] text-black font-bold"
                  data-testid="art-loc-palette-save">Save</button>
                <button type="button" onClick={() => setAddingColor(false)}
                  className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)]">Cancel</button>
              </div>
            )}
          </div>
        </div>
        <div className="px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mb-1.5">Map</div>
          {editingAddress ? (
            <div className="flex gap-1 items-center">
              <Input value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="Street, city, country…"
                className="bg-[var(--color-bg)] border border-[var(--color-border)] text-[12px] h-7 text-[var(--color-text)]"
                data-testid="art-loc-address-input" />
              <button type="button" onClick={saveAddress}
                className="text-[10px] px-2 py-1 rounded bg-[var(--color-accent)] text-black font-bold"
                data-testid="art-loc-address-save">Save</button>
              <button type="button" onClick={() => { setEditingAddress(false); setAddress(location.address || ''); }}
                className="text-[10px] px-2 py-1 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)]">Cancel</button>
            </div>
          ) : location.address ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-[var(--color-text-sec)] truncate">{location.address}</span>
              {canWrite && (
                <button type="button" onClick={() => setEditingAddress(true)}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-text-dim)] hover:text-[var(--color-accent)]">
                  Edit
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-[11px] italic text-[var(--color-text-mute)]">No address set.</span>
              {canWrite && (
                <button type="button" onClick={() => setEditingAddress(true)}
                  className="text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)]"
                  data-testid="art-loc-add-address">+ Add address</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* G. Base Kit — Phase 6.5 real UI */}
      <div className="px-3 py-2 rounded-b-lg" data-testid="art-loc-base-kit-section">
        <div className="flex items-baseline gap-2 mb-1.5">
          <Layers className="w-3.5 h-3.5 text-[var(--color-set-dressing)]" />
          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-set-dressing)]">
            Base Kit (permanent fixtures at this location)
          </span>
          <span className="text-[10px] text-[var(--color-text-dim)]">({(location.base_kit_asset_ids || []).length})</span>
          {canWrite && (
            <div className="ml-auto flex gap-1">
              <button type="button"
                onClick={async () => {
                  const lid = await ensureLocationId();
                  const other = (assets || []).filter(a => !(location.base_kit_asset_ids || []).includes(a.id));
                  if (other.length === 0) { toast.info('No existing assets to add. Create one first.'); return; }
                  const label = other.map((a, i) => `${i + 1}. ${a.name}`).join('\n');
                  const pick = window.prompt(
                    `Pick an asset to add to Base Kit:\n\n${label}\n\nEnter number:`,
                  );
                  const n = parseInt(pick || '', 10);
                  if (!Number.isFinite(n) || n < 1 || n > other.length) return;
                  const asset = other[n - 1];
                  const nextIds = [...(location.base_kit_asset_ids || []), asset.id];
                  try {
                    const r = await authAxios.patch(
                      `/projects/${projectId}/art/locations/${lid}`,
                      { base_kit_asset_ids: nextIds },
                    );
                    onDataChange?.('location', r.data);
                    toast.success(`Added "${asset.name}" to Base Kit`);
                  } catch (e) { console.error(e); toast.error('Update failed'); }
                }}
                className="text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-set-dressing)] hover:bg-[var(--color-panel)]"
                data-testid="art-loc-basekit-add-existing">
                + Add existing
              </button>
              <button type="button"
                onClick={async () => {
                  const lid = await ensureLocationId();
                  openAssetModal({
                    prefill: { linked_location_ids: [lid], category: 'set_dressing' },
                    onCreated: async (saved) => {
                      const nextIds = [...(location.base_kit_asset_ids || []), saved.id];
                      try {
                        const r = await authAxios.patch(
                          `/projects/${projectId}/art/locations/${lid}`,
                          { base_kit_asset_ids: nextIds },
                        );
                        onDataChange?.('location', r.data);
                        toast.success('Added to Base Kit');
                      } catch (e) { console.error(e); toast.error('Update failed'); }
                    },
                  });
                }}
                className="text-[10px] px-2 py-0.5 rounded border border-[var(--color-set-dressing)] bg-[var(--color-set-dressing)]/10 text-[var(--color-set-dressing)] hover:bg-[var(--color-set-dressing)]/20"
                data-testid="art-loc-basekit-add-new">
                + Create new
              </button>
            </div>
          )}
        </div>
        {(location.base_kit_asset_ids || []).length === 0 ? (
          <div className="text-[11px] italic text-[var(--color-text-mute)]">
            No Base Kit yet.{canWrite && ' Add permanent fixtures — they\'ll auto-appear on every scene at this location.'}
          </div>
        ) : (
          <div className="flex gap-1 flex-wrap">
            {(location.base_kit_asset_ids || []).map(aid => {
              const a = (assets || []).find(x => x.id === aid);
              if (!a) return null;
              const firstPath = a.photos?.[0]?.storage_path;
              return (
                <div key={aid}
                  className="inline-flex items-center gap-1.5 bg-[var(--color-panel)] border border-[var(--color-set-dressing)]/40 rounded-full pl-1 pr-2 py-1 group"
                  data-testid={`art-loc-basekit-chip-${aid}`}>
                  {firstPath && (
                    <div className="w-5 h-5 rounded-full overflow-hidden border border-[var(--color-border)] shrink-0">
                      <AuthImage path={firstPath} className="w-full h-full object-cover" alt="" />
                    </div>
                  )}
                  <Link2 className="w-3 h-3 text-[var(--color-set-dressing)]" />
                  <span className="text-[11px] text-[#c8e6c8]">{a.name}</span>
                  {canWrite && (
                    <button type="button"
                      onClick={async () => {
                        if (!window.confirm(`Remove "${a.name}" from ${location.name}'s Base Kit?\n\nEvery scene at this location will lose the chip.`)) return;
                        const nextIds = (location.base_kit_asset_ids || []).filter(x => x !== aid);
                        try {
                          const r = await authAxios.patch(
                            `/projects/${projectId}/art/locations/${location.id}`,
                            { base_kit_asset_ids: nextIds },
                          );
                          onDataChange?.('location', r.data);
                          toast.success('Removed from Base Kit');
                        } catch (e) { console.error(e); toast.error('Remove failed'); }
                      }}
                      className="opacity-0 group-hover:opacity-100 text-[var(--color-text-dim)] hover:text-[var(--color-priority-critical)]"
                      title="Remove from Base Kit"
                      data-testid={`art-loc-basekit-remove-${aid}`}>
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Phase 9 — Location-side Special Requirements rollup (read-only chips + create) */}
      <LocationRequirementsRollup
        locationId={location.id}
        locationName={location.name}
        projectId={projectId}
        token={token}
        canWrite={canWrite}
        ensureLocationId={ensureLocationId}
      />
    </div>
  );
}

// ── Phase 9: Read-only rollup of Special Requirements linked to this location ──
function LocationRequirementsRollup({ locationId, locationName, projectId, token, canWrite, ensureLocationId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);
  const { openRequirementModal } = useRequirementActions();

  const reload = useCallback(async () => {
    if (!locationId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await authAxios.get(
        `/projects/${projectId}/art/requirements?linked_location_id=${locationId}`,
      );
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authAxios, projectId, locationId]);
  useEffect(() => { reload(); }, [reload]);

  const onCreated = useCallback((saved) => {
    setItems(prev => {
      const i = prev.findIndex(x => x.id === saved.id);
      if (i < 0) return [saved, ...prev];
      const nx = prev.slice(); nx[i] = saved; return nx;
    });
  }, []);

  const linkExisting = async () => {
    try {
      const lid = await ensureLocationId();
      const r = await authAxios.get(`/projects/${projectId}/art/requirements`);
      const all = r.data.items || [];
      const unlinked = all.filter(x => !(x.linked_location_ids || []).includes(lid));
      if (unlinked.length === 0) { toast.info('No other requirements to link.'); return; }
      const label = unlinked.map((x, i) => `${i + 1}. [${(x.priority || 'std').toUpperCase()}] ${x.title}`).join('\n');
      const pick = window.prompt(`Link an existing requirement:\n\n${label}\n\nEnter number:`);
      const n = parseInt(pick || '', 10);
      if (!Number.isFinite(n) || n < 1 || n > unlinked.length) return;
      const chosen = unlinked[n - 1];
      const nextIds = [...(chosen.linked_location_ids || []), lid];
      const patched = await authAxios.patch(
        `/projects/${projectId}/art/requirements/${chosen.id}`,
        { linked_location_ids: nextIds },
      );
      onCreated(patched.data);
      toast.success('Linked to location');
    } catch (e) { console.error(e); toast.error('Link failed'); }
  };

  const rowClick = (req) => {
    openRequirementModal({ mode: 'edit', req, onCreated });
  };

  const empty = !loading && items.length === 0;
  if (empty && !canWrite) return null;

  return (
    <div className="px-3 py-2" data-testid={`art-loc-requirements-rollup-${locationName.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-accent)]">
          Special Requirements
        </span>
        <span className="text-[10px] text-[var(--color-text-dim)]">({items.length})</span>
      </div>
      {loading && <div className="text-[11px] italic text-[var(--color-text-mute)]">Loading…</div>}
      {empty && (
        <div className="text-[11px] italic text-[var(--color-text-mute)] mb-2">
          No requirements linked to this location yet.
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map(r => (
            <button key={r.id} type="button" onClick={() => rowClick(r)}
              className="w-full text-left flex items-center gap-2 py-1 px-2 rounded hover:bg-[var(--color-panel)]"
              data-testid={`art-loc-req-row-${r.id}`}>
              <ReqPriorityPill priority={r.priority} />
              <ReqCategoryPill category={r.category} />
              <span className="text-[11px] text-[var(--color-text)] flex-1 truncate">{r.title}</span>
              <ReqStatusPill status={r.status} />
            </button>
          ))}
        </div>
      )}
      {canWrite && (
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={linkExisting}
            className="text-[10px] px-2 py-1 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
            data-testid={`art-loc-req-link-btn-${locationName.toLowerCase().replace(/\s+/g,'-')}`}>
            + Link requirement
          </button>
          <button type="button"
            onClick={async () => {
              const lid = await ensureLocationId();
              openRequirementModal({
                mode: 'create',
                prefill: { linked_location_ids: [lid] },
                onCreated,
              });
            }}
            className="text-[10px] px-2 py-1 rounded border border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20"
            data-testid={`art-loc-req-new-btn-${locationName.toLowerCase().replace(/\s+/g,'-')}`}>
            + New requirement (this location)
          </button>
        </div>
      )}
    </div>
  );
}

// ── Card view: 3-column grid ───────────────────────────────────────────
function LocationCard({ location, palettes, locationPhotos, onOpen }) {
  const hero = locationPhotos[0];
  const pinnedPalette = (palettes || []).find(p => p.scope === 'location' && p.scope_id === location.id);
  return (
    <button type="button"
      onClick={() => onOpen(location)}
      className="text-left border border-[var(--color-border)] rounded-lg bg-[var(--color-panel)] hover:border-[var(--color-accent)] overflow-hidden transition"
      data-testid={`art-loc-card-${location.name.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="relative aspect-[16/9] bg-[var(--color-bg)]">
        {hero ? (
          <AuthImage path={hero.storage_path} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--color-text-mute)]">
            <MapPin className="w-8 h-8" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1">
          {location.scene_int_ext === 'MIXED' ? (
            <>
              <TypeBadge type="INT" />
              <TypeBadge type="EXT" />
            </>
          ) : (
            <TypeBadge type={location.scene_int_ext || 'INT'} />
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
          <div className="text-sm font-bold text-white truncate">{location.name}</div>
        </div>
      </div>
      <div className="p-2">
        <div className="text-[11px] italic text-[var(--color-text-dim)] line-clamp-1 mb-1">{location.address || '—'}</div>
        <div className="text-[10px] text-[var(--color-text-dim)] mb-2 flex gap-2">
          <span>🎬 {location.scene_count} scenes</span>
          <span>📷 {location.photo_count} photos</span>
          <span>🗺 map</span>
        </div>
        <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mb-0.5">Scenes</div>
        <div className="flex gap-1 flex-wrap mb-2">
          {(location.scene_numbers || []).slice(0, 5).map(sn => (
            <span key={sn} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text-sec)]">
              #{formatSceneNumber(sn)}
            </span>
          ))}
          {(location.scene_numbers || []).length > 5 && (
            <span className="text-[10px] text-[var(--color-accent)]">+{location.scene_numbers.length - 5}</span>
          )}
        </div>
        <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mb-0.5">Palette</div>
        <div className="flex gap-1 mb-2">
          {pinnedPalette && (pinnedPalette.colors || []).slice(0, 5).map((c, i) => (
            <div key={i} className="w-4 h-4 rounded-full border border-[var(--color-border)]" style={{ background: c.hex }} />
          ))}
          {!pinnedPalette && <span className="text-[10px] italic text-[var(--color-text-mute)]">—</span>}
        </div>
        <div className="text-[11px] text-[var(--color-accent)] text-right">Open location →</div>
      </div>
    </button>
  );
}

// ── Main tab component ───────────────────────────────────────────────
export default function ArtByLocation({ projectId, token }) {
  const [view, setView] = useState(() => localStorage.getItem('art.by_location.view') || 'list');
  useEffect(() => { localStorage.setItem('art.by_location.view', view); }, [view]);

  const [locations, setLocations] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [items, setItems] = useState([]);
  const [palettes, setPalettes] = useState([]);
  const [moods, setMoods] = useState([]);
  const [galleryRefs, setGalleryRefs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnused, setShowUnused] = useState(false);
  const [search, setSearch] = useState('');

  const { permissions } = useProjectRole(projectId);
  const { user } = useAuth();
  // Art module (2026-07) — write affordances open to any authenticated user
  // with project access. Backend still enforces auth.
  const canWrite = !!user || !!permissions?.canWrite;

  // Drawer state (uses the same Drawer component as ArtByScene)
  const [drawerScene, setDrawerScene] = useState(null);

  // Lightbox state
  const [lbItems, setLbItems] = useState([]);
  const [lbIndex, setLbIndex] = useState(-1);
  const [lbCaption, setLbCaption] = useState(null);

  // Phase 12a Item 4 — orphan-button intent listener. The legacy
  // ArtDepartmentPage sidebar dispatches `art:switch-tab` with an
  // `intent` field; ArtDepartmentPage handles the tab switch itself,
  // and this listener consumes the intent to toast a brief hint about
  // the target surface. Heavy interaction (file picker, modal) is
  // handled by the location block's own buttons — we just steer the
  // user there.
  const [pendingIntent, setPendingIntent] = useState(null);
  useEffect(() => {
    const onSwitch = (e) => {
      if (e?.detail?.tab === 'by_location' && e?.detail?.intent) {
        setPendingIntent(e.detail.intent);
      }
    };
    window.addEventListener('art:switch-tab', onSwitch);
    return () => window.removeEventListener('art:switch-tab', onSwitch);
  }, []);
  useEffect(() => {
    if (!pendingIntent || locations.length === 0) return;
    const map = {
      mood_board:     'Scroll to any location block and use Mood Board upload.',
      upload_photo:   'Scroll to any location and use Upload Photo.',
      add_to_base_kit:'Scroll to any location and use + Add to Base Kit.',
      add_requirement:'Use + New requirement in a location panel.',
    };
    const msg = map[pendingIntent];
    if (msg) toast.info(msg);
    setPendingIntent(null);
  }, [pendingIntent, locations.length]);

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [locs, br, pal, mb, gal, ast] = await Promise.all([
        authAxios.get(`/projects/${projectId}/art/locations`),
        authAxios.get(`/projects/${projectId}/breakdown`),
        authAxios.get(`/projects/${projectId}/palettes?department=ART`).catch(() => ({ data: [] })),
        authAxios.get(`/projects/${projectId}/mood-board?department=ART`).catch(() => ({ data: [] })),
        authAxios.get(`/projects/${projectId}/gallery?department=ART`).catch(() => ({ data: [] })),
        authAxios.get(`/projects/${projectId}/art/assets`).catch(() => ({ data: { items: [] } })),
      ]);
      setLocations(locs.data || []);
      const sceneList = Array.isArray(br.data) ? br.data : (br.data?.scenes || []);
      setScenes(sceneList);
      const flat = [];
      for (const s of sceneList) {
        const bd = s.breakdown_items;
        if (Array.isArray(bd)) {
          for (const it of bd) flat.push({ ...it, scene_id: it.scene_id || s.id });
        } else if (bd && typeof bd === 'object') {
          for (const catArr of Object.values(bd)) {
            for (const it of (catArr || [])) flat.push({ ...it, scene_id: it.scene_id || s.id });
          }
        }
      }
      setItems(flat);
      setPalettes(pal.data || []);
      setMoods(mb.data?.images || mb.data || []);
      // gallery is grouped by location → flatten to a flat ref list
      const flatRefs = [];
      for (const loc of (gal.data || [])) {
        for (const room of (loc.rooms || [])) {
          for (const r of (room.items || [])) flatRefs.push({ ...r, location_name: loc.location_name });
        }
      }
      setGalleryRefs(flatRefs);
      setAssets(ast.data?.items || []);
    } catch (e) {
      console.error('ArtByLocation load failed', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const scenesByLocKey = useMemo(() => {
    const map = {};
    for (const s of scenes) {
      const k = (s.location_name || 'Unknown').trim().toLowerCase();
      (map[k] ||= []).push(s);
    }
    return map;
  }, [scenes]);

  const itemsByScene = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!it.scene_id) continue;
      (map[it.scene_id] ||= []).push(it);
    }
    return map;
  }, [items]);

  const refsByLocKey = useMemo(() => {
    const map = {};
    for (const r of galleryRefs) {
      const k = (r.location_name || 'General').trim().toLowerCase();
      (map[k] ||= []).push(r);
    }
    return map;
  }, [galleryRefs]);

  const filteredLocations = useMemo(() => {
    let list = locations.filter(l => showUnused || l.scene_count > 0);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(l => l.name.toLowerCase().includes(q) || (l.address || '').toLowerCase().includes(q));
    }
    return list;
  }, [locations, showUnused, search]);

  const unusedCount = locations.filter(l => l.scene_count === 0).length;

  const onDataChange = () => reload();

  const openLocationLightbox = useCallback((entry) => {
    // entry is { path, itemName, locationName }
    // Walk the current location's photos + moods to build the pager list.
    const key = (entry.locationName || '').trim().toLowerCase();
    const loc = locations.find(l => l.name.trim().toLowerCase() === key);
    const walk = [];
    for (const r of (refsByLocKey[key] || [])) {
      walk.push({ path: r.storage_path, itemName: r.original_filename || 'Photo', sceneNumber: undefined });
    }
    if (loc?.id) {
      for (const m of moods.filter(m => m.scope === 'location' && m.scope_id === loc.id)) {
        walk.push({ path: m.storage_path, itemName: m.caption || 'Mood', sceneNumber: undefined });
      }
    }
    const idx = Math.max(0, walk.findIndex(w => w.path === entry.path));
    setLbItems(walk);
    setLbCaption(`Location · ${loc?.name || entry.locationName || '—'}`);
    setLbIndex(idx >= 0 ? idx : 0);
  }, [locations, refsByLocKey, moods]);

  const openScenePhotoLightbox = useCallback((item, photoIndex) => {
    const paths = item.image_paths || [];
    if (!paths.length) return;
    setLbItems(paths.map((p, i) => ({ path: p, itemName: item.description || '—', sceneNumber: item.scene_number })));
    setLbCaption(null);
    setLbIndex(photoIndex || 0);
  }, []);

  // Drawer mutation callbacks so edits made via the drawer reflect here
  const handleSceneMutate = useCallback((updated) => {
    setScenes(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
    setDrawerScene(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
  }, []);
  const handleItemMutate = useCallback((op, item) => {
    if (op === 'add')    setItems(prev => [...prev, item]);
    if (op === 'update') setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...item } : i));
    if (op === 'delete') setItems(prev => prev.filter(i => i.id !== item.id));
  }, []);
  const handlePaletteMutate = useCallback((op, p) => {
    if (op === 'add')    setPalettes(prev => [...prev, p]);
    if (op === 'delete') setPalettes(prev => prev.filter(x => x.id !== p.id));
  }, []);
  const handleMoodMutate = useCallback((op, m) => {
    if (op === 'add')    setMoods(prev => [...prev, m]);
    if (op === 'delete') setMoods(prev => prev.filter(x => x.id !== m.id));
  }, []);

  // Drawer bucket + scoped palettes/moods (with Phase 6.5 Base Kit
  // inheritance mixed in — same read-time join as ArtByScene).
  const drawerBuckets = useMemo(() => {
    if (!drawerScene) return { action_props: [], scene_props: [], set_dressing: [], picture_vehicle: [] };
    const b = bucketsForItems(itemsByScene[drawerScene.id]);
    const locKey = (drawerScene.location_name || '').trim().toLowerCase();
    const loc = locations.find(l => (l.name || '').trim().toLowerCase() === locKey);
    if (loc && (loc.base_kit_asset_ids?.length)) {
      const excluded = new Set(drawerScene.excluded_base_kit_ids || []);
      const inherited = loc.base_kit_asset_ids
        .filter(id => !excluded.has(id))
        .map(id => (assets || []).find(a => a.id === id))
        .filter(Boolean);
      for (const a of inherited) {
        const bucket = a.category === 'action_prop' ? 'action_props'
          : a.category === 'set_dressing' ? 'set_dressing'
          : a.category === 'picture_vehicle' ? 'picture_vehicle'
          : 'scene_props';
        b[bucket].unshift({
          id: `inherited-${a.id}`,
          __inherited_from_base_kit: true,
          __asset_id: a.id,
          description: a.name,
          image_paths: (a.photos || []).map(p => p.storage_path),
          category: bucket === 'set_dressing' ? 'ART_SET_DRESSING'
            : bucket === 'picture_vehicle' ? 'PICTURE_VEHICLE' : 'PROP',
          asset_sub_type: bucket === 'action_props' ? 'action_prop' : undefined,
        });
      }
    }
    return b;
  }, [drawerScene, itemsByScene, locations, assets]);
  const drawerPalettes = useMemo(() =>
    drawerScene ? palettes.filter(p => p.scope === 'scene' && p.scope_id === drawerScene.id) : [],
  [drawerScene, palettes]);
  const drawerMoods = useMemo(() =>
    drawerScene ? moods.filter(m => m.scope === 'scene' && m.scope_id === drawerScene.id) : [],
  [drawerScene, moods]);

  // Cards → List anchor scroll
  const listWrapRef = useRef(null);
  const [anchorLoc, setAnchorLoc] = useState(null);
  useEffect(() => {
    if (view === 'list' && anchorLoc && listWrapRef.current) {
      const slug = anchorLoc.toLowerCase().replace(/\s+/g,'-');
      const el = listWrapRef.current.querySelector(`[data-testid="art-loc-block-${slug}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setAnchorLoc(null);
    }
  }, [view, anchorLoc, filteredLocations]);

  return (
    <div className="p-4" data-testid="art-by-location-view">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1 bg-[var(--color-bg)]">
          <Search className="w-3 h-3 text-[var(--color-text-dim)]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search location or address"
            className="bg-transparent border-0 h-6 text-xs w-56"
            data-testid="art-loc-search-input" />
        </div>
        <div className="flex gap-1 border border-[var(--color-border)] rounded overflow-hidden">
          <button onClick={() => setView('list')}
            className={`px-3 py-1 text-xs ${view === 'list' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
            data-testid="art-loc-view-list">List</button>
          <button onClick={() => setView('cards')}
            className={`px-3 py-1 text-xs ${view === 'cards' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
            data-testid="art-loc-view-cards">Cards</button>
        </div>
        <span className="text-[10px] text-[var(--color-text-dim)] ml-2">{filteredLocations.length} location{filteredLocations.length === 1 ? '' : 's'}</span>
      </div>

      {loading && <div className="text-center text-[var(--color-text-dim)] text-sm py-8">Loading…</div>}
      {!loading && filteredLocations.length === 0 && (
        <div className="text-center text-[var(--color-text-dim)] text-sm py-12" data-testid="art-loc-empty">
          No locations found. Add a location by naming one on any scene.
        </div>
      )}

      {!loading && view === 'list' && (
        <div ref={listWrapRef} className="space-y-4">
          {filteredLocations.map(loc => (
            <LocationBlock
              key={loc.name}
              location={loc}
              scenes={scenesByLocKey[(loc.name || '').trim().toLowerCase()] || []}
              itemsByScene={itemsByScene}
              palettes={palettes}
              moods={moods}
              locationPhotos={refsByLocKey[(loc.name || '').trim().toLowerCase()] || []}
              assets={assets}
              canWrite={canWrite}
              onOpenDrawer={setDrawerScene}
              projectId={projectId}
              token={token}
              onDataChange={onDataChange}
              onPhotoClick={(e) => { openLocationLightbox(e); }}
            />
          ))}
          {unusedCount > 0 && (
            <button type="button" onClick={() => setShowUnused(v => !v)}
              className="w-full text-center py-2 text-[11px] text-[var(--color-accent)] hover:text-white"
              data-testid="art-loc-toggle-unused">
              {showUnused ? '▴ Hide' : '▾ Show'} {unusedCount} unused location{unusedCount === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}

      {!loading && view === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLocations.map(loc => (
            <LocationCard
              key={loc.name}
              location={loc}
              palettes={palettes}
              locationPhotos={refsByLocKey[(loc.name || '').trim().toLowerCase()] || []}
              onOpen={(l) => {
                setAnchorLoc(l.name);
                setView('list');
              }}
            />
          ))}
        </div>
      )}

      {/* Drawer — reuses ArtByScene's Drawer */}
      {drawerScene && (
        <SceneDrawer
          scene={drawerScene}
          buckets={drawerBuckets}
          palettes={drawerPalettes}
          moods={drawerMoods}
          onClose={() => setDrawerScene(null)}
          onPhotoClick={openScenePhotoLightbox}
          canWrite={canWrite}
          projectId={projectId}
          token={token}
          onSceneMutate={handleSceneMutate}
          onItemMutate={handleItemMutate}
          onPaletteMutate={handlePaletteMutate}
          onMoodMutate={handleMoodMutate}
          locations={locations}
          setLocations={setLocations}
        />
      )}

      <Lightbox
        items={lbItems}
        index={lbIndex}
        onClose={() => setLbIndex(-1)}
        onNav={(delta) => setLbIndex(i => {
          if (!lbItems.length) return -1;
          if (typeof delta === 'number' && (delta > 1 || delta < -1)) {
            // jump target — clamp
            const n = i + delta;
            return Math.max(0, Math.min(lbItems.length - 1, n));
          }
          return (i + delta + lbItems.length) % lbItems.length;
        })}
        captionPrefix={lbCaption}
      />
    </div>
  );
}
